import { Injectable, inject } from '@angular/core';
import {
  combineLatestWith,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import {
  BoelsPointOfService,
  AppConfig,
  GeoPoint,
  Region,
  Specialism,
  SpecialismList,
  StoreCountCountryPayload,
  StoreFinderSearchPage,
} from '@boels/models';
import { StoreFinderGateway } from '../gateway/store-finder-gateway.service';
import { TranslocoService } from '@jsverse/transloco';
import { UrlSafePipe } from '@boels/helpers';
import { select, Store } from '@ngrx/store';
import {
  setActiveRegion,
  setActiveStore,
  setSpecialismList,
} from '../actions/store-finder.actions';
import {
  selectActiveRegion,
  selectActiveStoreFinder,
  selectSpecialismList,
} from '../selectors/store-finder.selectors';
import { CultureFacade } from '../../cultures/facade/cultures.facade';

@Injectable({
  providedIn: 'root',
})
export class StoreFinderFacade {
  #storeFinderGateway = inject(StoreFinderGateway);
  #environment = inject<AppConfig>('ENVIRONMENT' as any);
  #translocoService = inject(TranslocoService);
  #urlSafePipe = inject(UrlSafePipe);
  #cultureFacade = inject(CultureFacade);
  readonly #store = inject(Store);

  active$: Observable<BoelsPointOfService | undefined> = this.#store.select(
    selectActiveStoreFinder,
  );

  getPointOfService(
    pointOfServiceName: string,
  ): Observable<BoelsPointOfService> {
    return this.#storeFinderGateway.getPointOfService(pointOfServiceName).pipe(
      map((store) => ({
        ...store,
        formattedAddress: this.getFormattedStoreAddress([
          store.address?.town,
          store.address?.postalCode,
          store.address?.country?.isocode,
        ]),
        specialisms: this.getFullMediaUrl(store.specialisms),
      })),
    );
  }

  getPointOfServicesByQuery(
    query: string,
    pageSize?: number,
    specialism?: string,
  ): Observable<StoreFinderSearchPage> {
    return this.#storeFinderGateway
      .getPointOfServicesByQuery(query, pageSize, specialism)
      .pipe(
        map((storeFinderSearchPage: StoreFinderSearchPage) =>
          this.prepareStoreFinderSearchPage(storeFinderSearchPage),
        ),
      );
  }

  getPointOfServicesByGeoPoint(
    geoPoint: GeoPoint,
    pageSize?: number,
    specialism?: string,
  ): Observable<StoreFinderSearchPage> {
    return this.#storeFinderGateway
      .getPointOfServicesByGeoPoint(geoPoint, pageSize, specialism)
      .pipe(
        map((storeFinderSearchPage: StoreFinderSearchPage) =>
          this.prepareStoreFinderSearchPage(storeFinderSearchPage),
        ),
      );
  }

  getStoresByRegion(): Observable<Region[]> {
    return this.#storeFinderGateway.getStoresByRegion().pipe(
      map((boelsCountryPayload: StoreCountCountryPayload) => {
        return (
          boelsCountryPayload.countriesAndRegionsStoreCount?.[0]
            ?.storeCountDataList ?? []
        );
      }),
      shareReplay(1),
    );
  }

  getStoreFinderSpecialisms(): Observable<SpecialismList> {
    return this.#store
      .pipe(
        select(selectSpecialismList),
        withLatestFrom(this.#cultureFacade.getActiveLanguage()),
        switchMap(([specialism, language]) => {
          if (specialism && specialism.lang === language) return of(specialism);
          return this.#storeFinderGateway.getStoreFinderSpecialisms().pipe(
            switchMap((specialismList) => {
              this.#store.dispatch(
                setSpecialismList({ lang: language, specialismList }),
              );
              return this.#store.pipe(
                select(selectSpecialismList),
                filter(Boolean),
                filter((specialism) => specialism.lang === language),
              );
            }),
          );
        }),
      )
      .pipe(
        combineLatestWith(
          this.#translocoService.selectTranslate(
            'specialism.allSpecialisms',
            undefined,
            'storeFinder',
          ),
        ),
        map(([specialism, translation]) => ({
          specialisms: [
            { name: translation, code: '0' },
            ...this.sortSpecialismsByName(
              specialism.specialismList?.specialisms ?? [],
            ),
          ],
        })),
        shareReplay(1),
      );
  }

  private prepareStoreFinderSearchPage(
    storeFinderSearchPage: StoreFinderSearchPage,
  ): StoreFinderSearchPage {
    return {
      ...storeFinderSearchPage,
      stores: storeFinderSearchPage.stores?.map((store) => ({
        ...store,
        formattedAddress: this.getFormattedStoreAddress([
          store.address?.town,
          store.address?.postalCode,
          store.address?.country?.isocode,
        ]),
        url: `${this.#urlSafePipe.transform(store.address?.region?.name)}/${
          store.displayNameSlug
        }`,
      })),
    };
  }

  private getFormattedStoreAddress(
    addressParts: (string | undefined)[],
  ): string {
    return addressParts?.filter(Boolean).join(', ');
  }

  private getFullMediaUrl(
    specialisms: Specialism[] | undefined,
  ): Specialism[] | undefined {
    const { baseUrl } = this.#environment.backend.occ;
    if (specialisms) {
      return specialisms.map((specialism) => ({
        ...specialism,
        icon: { ...specialism.icon, url: `${baseUrl}/${specialism.icon?.url}` },
      }));
    }
    return undefined;
  }

  private sortSpecialismsByName(specialisms: Specialism[]): Specialism[] {
    return specialisms.sort((a: Specialism, b: Specialism) => {
      if (a.name === undefined && b.name === undefined) {
        return 0;
      }

      if (a.name === undefined) {
        return 1;
      }

      if (b.name === undefined) {
        return -1;
      }

      return a.name.localeCompare(b.name);
    });
  }

  setActiveStore(store: BoelsPointOfService | undefined): void {
    this.#store.dispatch(setActiveStore({ store }));
  }

  getActiveStore(): Observable<BoelsPointOfService | undefined> {
    return this.#store.pipe(select(selectActiveStoreFinder));
  }

  setActiveRegion(region: string | null): void {
    this.#store.dispatch(setActiveRegion({ region }));
  }

  getActiveRegion(): Observable<string | undefined | null> {
    return this.#store.pipe(select(selectActiveRegion));
  }
}
